/** @forge-header
 * @id          seriesParser
 * @role        Playoff series-winner ticker parser | NBA/NHL/MLB
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,TickerCategory,PlayoffSeriesInfo | kalshiTicker/teamNames:teamNameFromCode | kalshiTicker/helpers:parseFallback
 * @downstream  kalshiTicker/parse
 * @exports     parseSeriesWinner:fn
 * @test        untested
 */

import type { GameInfo, ParsedTicker, TickerCategory } from '../types';
import { parseFallback } from '../helpers';
import { teamNameFromCode } from '../teamNames';

/**
 * Playoff series-winner: KX{NBA|NHL|MLB}SERIES-{YY}{TEAM1}{TEAM2}R{N}-{OUTCOME}
 *
 *   KXNBASERIES-26PHIBOSR1-BOS    → BOS at R1, teams = PHI vs BOS
 *   KXNHLSERIES-26VGKANAR2-ANA    → ANA at R2, teams = VGK vs ANA
 *   KXMLBSERIES-26HOUSEARW-SEA    → (would parse if such a shape existed)
 *
 * Team codes are variable-length (NHL has 2- and 3-letter codes; MLB/NBA
 * are 3) so we use the trailing outcome code to pick the split point —
 * mirrors the same trick the marketStore parser uses.
 */
const SERIES_RE = /^KX(NBA|WNBA|NHL|MLB)SERIES-(\d{2})([A-Z]+)R(\d)-([A-Z0-9]+)$/;

const SPORT_TO_CATEGORY: Record<string, TickerCategory> = {
  NBA: 'nba-series',
  WNBA: 'wnba-series',
  NHL: 'nhl-series',
  MLB: 'mlb-series',
};

const SPORT_TO_LOOKUP: Record<string, string> = {
  NBA: 'nba',
  WNBA: 'wnba',
  NHL: 'nhl',
  MLB: 'mlb',
};

export function parseSeriesWinner(raw: string): ParsedTicker {
  const m = raw.match(SERIES_RE);
  if (!m) return parseFallback(raw);

  const [, sportLetters, year, teamsPart, roundStr, outcomeCode] = m;
  const category = SPORT_TO_CATEGORY[sportLetters];
  const sportKey = SPORT_TO_LOOKUP[sportLetters];
  if (!category || !sportKey) return parseFallback(raw);

  // Outcome code anchors the split. It must appear at the start or end
  // of the teams chunk; otherwise team-code lengths are ambiguous.
  let team1: string | null = null;
  let team2: string | null = null;
  if (teamsPart.startsWith(outcomeCode)) {
    team1 = outcomeCode;
    team2 = teamsPart.slice(outcomeCode.length);
  } else if (teamsPart.endsWith(outcomeCode)) {
    team1 = teamsPart.slice(0, teamsPart.length - outcomeCode.length);
    team2 = outcomeCode;
  }
  if (!team1 || !team2) return parseFallback(raw);

  const game: GameInfo = {
    awayCode: team1,
    homeCode: team2,
    awayName: teamNameFromCode(team1, sportKey) || undefined,
    homeName: teamNameFromCode(team2, sportKey) || undefined,
  };

  const round = parseInt(roundStr, 10);
  const series = `KX${sportLetters}SERIES`;
  const shortLabel = `${sportLetters}: ${team1} vs ${team2} R${round}`;
  const fullLabel = `${sportLetters}: ${game.awayName ?? team1} vs ${game.homeName ?? team2} — Round ${round} · ${outcomeCode} Wins`;

  return {
    raw,
    category,
    series,
    date: null,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: outcomeCode,
    shortLabel,
    fullLabel,
    playoffSeries: { round, year },
  };
}
